// src/odoo/customers/components/CustomerPurchaseTrendChart.tsx
// All comments are in English.

import React from "react";
import type { PurchaseTrendResponse } from "../api/overviewApi";
import { formatHKD } from "./utils";
import {
  ResponsiveContainer,
  LineChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  Line,
} from "recharts";

interface Props {
  data?: PurchaseTrendResponse;
  isLoading: boolean;
  error?: unknown;
}

const AXIS_COLOR = "#64748b";
const GMV_LINE_COLOR = "rgba(34, 197, 94, 0.9)";     // green
const ORDERS_LINE_COLOR = "rgba(239, 68, 68, 0.9)";  // red

export const CustomerPurchaseTrendChart: React.FC<Props> = ({
  data,
  isLoading,
  error,
}) => {
  const points = data?.points ?? [];

  if (isLoading) {
    return (
      <div className="p-4 border rounded-lg bg-card h-80 flex items-center justify-center">
        <p className="text-sm text-muted-foreground">Loading purchase trend…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 border rounded-lg bg-card h-80 flex items-center">
        <p className="text-sm text-red-600">Failed to load purchase trend.</p>
      </div>
    );
  }

  if (!points.length) {
    return (
      <div className="p-4 border rounded-lg bg-card h-80 flex items-center">
        <p className="text-sm text-muted-foreground">No purchase data available.</p>
      </div>
    );
  }

  return (
    <div className="p-4 border rounded-lg bg-card h-80 flex flex-col">
      {/* Title */}
      <h2 className="text-lg font-semibold mb-1">
        Purchase Trend (HKD)
      </h2>

      {/* Description for how to read this chart */}
      <p className="text-xs text-muted-foreground mb-3 leading-relaxed">
        This chart shows the customer’s purchasing behavior.
        The <span style={{ fontWeight: 600, color: "rgba(34, 197, 94, 0.9)" }}>GMV</span> line
        represents the total purchase amount, while the
        <span style={{ fontWeight: 600, color: "rgba(239, 68, 68, 0.9)" }}> Orders</span> line represents
        the number of orders. The left axis shows GMV in HKD, and the right axis
        shows order count. A rising
        <span style={{ fontWeight: 600, color: "rgba(34, 197, 94, 0.9)" }}> GMV</span> line indicates higher
        spending, while a rising
        <span style={{ fontWeight: 600, color: "rgba(239, 68, 68, 0.9)" }}> Orders</span> line indicates
        increased purchase frequency. Use this chart to identify seasonal patterns
        and evaluate both value and volume trends.
      </p>


      <div className="flex-1 min-w-0 h-full">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={points}
            margin={{ top: 10, right: 20, left: 0, bottom: 10 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(148, 163, 184, 0.4)" />
            <XAxis
              dataKey="period_label"
              tick={{ fontSize: 10, fill: AXIS_COLOR }}
              stroke={AXIS_COLOR}
              minTickGap={8}
            />
            <YAxis
              yAxisId="left"
              tick={{ fontSize: 10, fill: AXIS_COLOR }}
              stroke={AXIS_COLOR}
              width={90}
              tickFormatter={(value: number) =>
                `HKD ${value.toLocaleString()}`
              }
            />
            <YAxis
              yAxisId="right"
              orientation="right"
              tick={{ fontSize: 10, fill: AXIS_COLOR }}
              stroke={AXIS_COLOR}
              width={40}
              tickFormatter={(value: number) => value.toString()}
            />

            <Tooltip
              formatter={(value: any, name: string) => {
                if (name === "gmv") return [formatHKD(Number(value)), "GMV"];
                if (name === "order_count") return [value, "Orders"];
                if (name === "avg_order_value")
                  return [formatHKD(Number(value)), "Avg Order Value"];
                return [value, name];
              }}
              labelFormatter={(label) => `Month: ${label}`}
            />

            <Legend />

            <Line
              yAxisId="left"
              type="monotone"
              dataKey="gmv"
              name="GMV"
              stroke={GMV_LINE_COLOR}
              strokeWidth={2}
              dot={false}
            />

            <Line
              yAxisId="right"
              type="monotone"
              dataKey="order_count"
              name="Orders"
              stroke={ORDERS_LINE_COLOR}
              strokeWidth={2}
              strokeDasharray="4 2"
              dot={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default CustomerPurchaseTrendChart;
